import { playSlamPalette, type PlayRarity } from "./playSlamRarity.js";

export type SlamKind = "dot" | "star" | "streak";
export type SlamSpark = {
  x: number; y: number; vx: number; vy: number;
  life: number; maxLife: number; size: number;
  kind: SlamKind; rot: number; len: number; ax: number; ay: number;
  color: string;
};
export type SlamBox = { x: number; y: number; width: number; height: number };
export type SlamDensity = {
  top: number; side: number; extraDot: number; star: number; streak: number;
  starMin: number; starMax: number;
};

type Emitter = { x: number; y: number; dx: number; dy: number };
type Rng = () => number;

export function slamDensity(rarity: PlayRarity): SlamDensity {
  if (rarity === "small") {
    return { top: 4, side: 5, extraDot: 0.4, star: 0.28, streak: 0.45, starMin: 2.4, starMax: 4 };
  }
  return { top: 4, side: 5, extraDot: 0.5, star: 0.55, streak: 0.5, starMin: 3.2, starMax: 5.6 };
}

function rand(min: number, max: number, rng: Rng) {
  return min + rng() * (max - min);
}

function alongEdge(
  n: number, x0: number, y0: number, x1: number, y1: number, dx: number, dy: number,
): Emitter[] {
  const out: Emitter[] = [];
  for (let i = 0; i < n; i += 1) {
    const u = (i + 0.5) / n;
    out.push({ x: x0 + (x1 - x0) * u, y: y0 + (y1 - y0) * u, dx, dy });
  }
  return out;
}

function edgeGroups(box: SlamBox, density: SlamDensity): Emitter[][] {
  const { x, y, width, height } = box;
  const r = x + width;
  const b = y + height;
  return [
    alongEdge(density.top, x, y, r, y, 0, -1),
    alongEdge(density.side, r, y, r, b, 1, 0),
    alongEdge(density.top, r, b, x, b, 0, 1),
    alongEdge(density.side, x, b, x, y, -1, 0),
  ];
}

function spawnFromEdge(e: Emitter, d: SlamDensity, color: string, rng: Rng): SlamSpark[] {
  const out = [dot(e, color, rng)];
  if (rng() < d.extraDot) out.push(dot(e, color, rng));
  if (rng() < d.star) out.push(star(e, d, color, rng));
  if (rng() < d.streak) out.push(streak(e, color, rng));
  return out;
}

function dot(e: Emitter, color: string, rng: Rng): SlamSpark {
  const tangent = rand(-22, 22, rng);
  return {
    x: e.x + rand(-2, 2, rng), y: e.y + rand(-2, 2, rng),
    vx: e.dx * rand(22, 86, rng) + -e.dy * tangent,
    vy: e.dy * rand(22, 86, rng) + e.dx * tangent,
    life: 0, maxLife: rand(0.18, 0.38, rng), size: rand(0.7, 1.8, rng),
    kind: "dot", rot: 0, len: 0, ax: 0, ay: 0, color,
  };
}

function star(e: Emitter, d: SlamDensity, color: string, rng: Rng): SlamSpark {
  return {
    x: e.x + e.dx * rand(1, 5, rng), y: e.y + e.dy * rand(1, 5, rng),
    vx: e.dx * rand(8, 28, rng), vy: e.dy * rand(8, 28, rng),
    life: 0, maxLife: rand(0.34, 0.62, rng), size: rand(d.starMin, d.starMax, rng),
    kind: "star", rot: rand(0, Math.PI / 3, rng), len: 0, ax: 0, ay: 0, color,
  };
}

function streak(e: Emitter, color: string, rng: Rng): SlamSpark {
  return {
    x: e.x, y: e.y, vx: e.dx * 12, vy: e.dy * 12,
    life: 0, maxLife: rand(0.07, 0.15, rng), size: rand(0.6, 1.15, rng),
    kind: "streak", rot: 0, len: rand(8, 18, rng), ax: e.dx, ay: e.dy, color,
  };
}

export function spawnCardEdgeSparks(box: SlamBox, rarity: PlayRarity, rng: Rng = Math.random): SlamSpark[] {
  const density = slamDensity(rarity);
  const palette = playSlamPalette(rarity);
  const groups = edgeGroups(box, density);
  const out: SlamSpark[] = [];
  let index = 0;
  for (const group of groups) {
    for (const emitter of group) {
      out.push(...spawnFromEdge(emitter, density, palette[index % palette.length], rng));
      index += 1;
    }
  }
  return out;
}

export function stepSlamSparks(list: readonly SlamSpark[], dt: number): SlamSpark[] {
  const next: SlamSpark[] = [];
  for (const spark of list) {
    const life = spark.life + dt;
    if (life >= spark.maxLife) continue;
    const drag = spark.kind === "star" ? 3.2 : 2.1;
    next.push({
      ...spark,
      life,
      x: spark.x + spark.vx * dt,
      y: spark.y + spark.vy * dt,
      vx: spark.vx * (1 - drag * dt),
      vy: spark.vy * (1 - drag * dt),
      rot: spark.rot + dt * (spark.kind === "star" ? 2.4 : 0),
    });
  }
  return next;
}

function alphaOf(spark: SlamSpark) {
  return 1 - spark.life / spark.maxLife;
}

function drawDot(ctx: CanvasRenderingContext2D, spark: SlamSpark) {
  const a = alphaOf(spark);
  ctx.globalAlpha = a * 0.4;
  ctx.fillStyle = spark.color;
  ctx.beginPath();
  ctx.arc(spark.x, spark.y, spark.size * 2.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = a;
  ctx.beginPath();
  ctx.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2);
  ctx.fill();
}

function drawStar(ctx: CanvasRenderingContext2D, spark: SlamSpark) {
  const a = alphaOf(spark);
  const pulse = 0.7 + 0.3 * Math.abs(Math.sin(spark.life * 28));
  const outer = spark.size * pulse;
  const inner = outer * 0.22;
  ctx.save();
  ctx.translate(spark.x, spark.y);
  ctx.rotate(spark.rot);
  ctx.globalAlpha = a * 0.35;
  ctx.fillStyle = spark.color;
  starPath(ctx, outer * 1.55, inner * 1.4);
  ctx.fill();
  ctx.globalAlpha = a;
  starPath(ctx, outer, inner);
  ctx.fill();
  ctx.restore();
}

function starPath(ctx: CanvasRenderingContext2D, outer: number, inner: number) {
  ctx.beginPath();
  for (let i = 0; i < 8; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const ang = (i * Math.PI) / 4 - Math.PI / 2;
    if (i === 0) ctx.moveTo(Math.cos(ang) * r, Math.sin(ang) * r);
    else ctx.lineTo(Math.cos(ang) * r, Math.sin(ang) * r);
  }
  ctx.closePath();
}

function drawStreak(ctx: CanvasRenderingContext2D, spark: SlamSpark) {
  const a = alphaOf(spark);
  const len = spark.len * (0.45 + 0.55 * a);
  ctx.globalAlpha = a * 0.9;
  ctx.strokeStyle = spark.color;
  ctx.lineWidth = spark.size;
  ctx.beginPath();
  ctx.moveTo(spark.x, spark.y);
  ctx.lineTo(spark.x + spark.ax * len, spark.y + spark.ay * len);
  ctx.stroke();
}

export function drawSlamSparks(ctx: CanvasRenderingContext2D, w: number, h: number, sparks: readonly SlamSpark[]) {
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = "lighter";
  for (const spark of sparks) {
    if (spark.kind === "star") drawStar(ctx, spark);
    else if (spark.kind === "streak") drawStreak(ctx, spark);
    else drawDot(ctx, spark);
  }
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
}
